import { DateTime } from 'luxon'
import DashboardLayout from '../components/DashboardLayout'
import { useState } from 'react'
import {
    Box,
    Button,
    Paper,
    Popover,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material'
import SnoozeIcon from '@mui/icons-material/Snooze'
import AlarmOffIcon from '@mui/icons-material/AlarmOff'
import PeriodDatePicker from '@components/PeriodDatePicker'
import DatePicker from '@components/DatePicker'
import DownloadButton from '@components/DownloadButton'
import { downloadFile, formatDateForDatabase } from '@utils'
import DownloadErrorDialog from '@components/DownloadErrorDialog'
import { STAFF_REPORTS_URL } from '@enums/api'

const DEFAULT_FROM_DATE = DateTime.now().startOf('month')
const DEFAULT_TO_DATE = DateTime.now()
const REPORT_API_ENDPOINT = `${STAFF_REPORTS_URL}/inactive-users`

const InactiveUsersReport = () => {
    const [fromDate, setFromDate] = useState<DateTime | null | undefined>(
        DEFAULT_FROM_DATE
    )
    const [toDate, setToDate] = useState<DateTime | null | undefined>(
        DEFAULT_TO_DATE
    )
    const [userType, setUserType] = useState<'historic' | 'never'>('historic')

    const [anchorElement, setAnchorElement] =
        useState<HTMLButtonElement | null>(null)
    const popoverOpen = Boolean(anchorElement)

    const [downloading, setDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState<string | null>(null)

    const handleDownload = async () => {
        setDownloading(true)

        const from = formatDateForDatabase(fromDate, 'start')
        const to = formatDateForDatabase(toDate, 'end')

        const fileFrom = fromDate?.toFormat('yyyy-LL-dd')
        const fileTo = toDate?.toFormat('yyyy-LL-dd')
        const fileName = `active_${userType}_${fileFrom}-${fileTo}.xlsx`

        const query = `?from=${from}&to=${to}&type=${userType}`

        try {
            await downloadFile({
                acceptType:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                fileURL: REPORT_API_ENDPOINT + query,
                saveAsFileName: fileName,
            })
            setDownloading(false)
        } catch (error) {
            console.error(error)
            setDownloading(false)
            setDownloadError('Failed to download report')
        }
    }

    return (
        <DashboardLayout>
            <DownloadErrorDialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
                title={downloadError}
            />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <Paper
                    style={{
                        paddingInline: 32,
                        paddingBlock: 32,
                        textAlign: 'center',
                    }}
                >
                    <Stack direction="column">
                        <Typography variant="body1">
                            Choose a user type to check.
                        </Typography>
                        <ToggleButtonGroup
                            value={userType}
                            exclusive
                            onChange={(_, v) => {
                                if (v !== null) {
                                    setUserType(v)
                                }
                            }}
                            aria-label="period selection"
                            color="primary"
                            style={{ marginTop: 8 }}
                        >
                            <Tooltip title="Users who have used our services but stopped.">
                                <ToggleButton
                                    value="historic"
                                    aria-label="historic"
                                    sx={{
                                        width: '100%',
                                    }}
                                >
                                    <SnoozeIcon />
                                    <Typography variant="body1">
                                        Historic
                                    </Typography>
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title="Users who have never used our services.">
                                <ToggleButton
                                    value="never"
                                    aria-label="never"
                                    sx={{
                                        width: '100%',
                                    }}
                                >
                                    <AlarmOffIcon />
                                    <Typography variant="body1">
                                        Never
                                    </Typography>
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                        <Typography variant="body1" style={{ marginTop: 16 }}>
                            Choose a date range to check.
                        </Typography>
                        <Stack
                            direction="row"
                            style={{
                                marginTop: 8,
                            }}
                        >
                            <DatePicker
                                disabled={downloading}
                                label="From"
                                onChange={setFromDate}
                                value={fromDate}
                                maxDate={toDate}
                            />
                            <DatePicker
                                disabled={downloading}
                                label="To"
                                onChange={setToDate}
                                value={toDate}
                                minDate={fromDate}
                            />
                        </Stack>
                        <Box style={{ marginTop: 8 }}>
                            <Button
                                onClick={(e) =>
                                    setAnchorElement(e.currentTarget)
                                }
                            >
                                Quick Set
                            </Button>
                            <Popover
                                open={popoverOpen}
                                anchorEl={anchorElement}
                                onClose={() => setAnchorElement(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                            >
                                <Paper sx={{ padding: 1 }}>
                                    <PeriodDatePicker
                                        fromDate={fromDate}
                                        setFromDate={setFromDate}
                                        toDate={toDate}
                                        setToDate={setToDate}
                                    />
                                </Paper>
                            </Popover>
                        </Box>
                        <Box sx={{ marginTop: 2 }}>
                            <DownloadButton
                                downloading={downloading}
                                onClick={handleDownload}
                            >
                                Download report
                            </DownloadButton>
                            <Typography
                                variant="body1"
                                style={{ marginTop: 8 }}
                            >
                                <small>
                                    This will download a single-sheet{' '}
                                    <code>.xlsx</code> file
                                </small>
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </div>
        </DashboardLayout>
    )
}

export default InactiveUsersReport
